import { type ApiClient, type Result, isErr } from "../client";
import { HttpMethod } from "../client/types";
import { unifiedUriSchema } from "./schema";
import type { PresignedUrl } from "./types";

function stripExtension(fileName: string): string {
	const parts = fileName.split(".");
	return parts.slice(0, parts.length - 1).join(".");
}

export interface FileUploadResponse {
	unified_uri: string;
	url: PresignedUrl;
}

export class FileService {
	private _prefix = "/files";

	constructor(private client: ApiClient) {}

	public async getPresignedUploadUrl(
		name: string,
		mimeType: string,
		threadId?: string,
	): Promise<Result<FileUploadResponse>> {
		return this.client.post<FileUploadResponse>(`${this._prefix}/upload-url`, {
			data: {
				file_name: stripExtension(name),
				mime_type: mimeType,
				thread_id: threadId,
			},
		});
	}

	public async getPresignedDownloadUrl(
		unifiedUri: string,
	): Promise<Result<{ url: PresignedUrl }>> {
		return this.client.get<{ url: PresignedUrl }>(`${this._prefix}/download`, {
			query: {
				unified_uri: unifiedUri,
			},
		});
	}

	// NOTE: (bwilytsch): Potentially migrate this to Fetch API with an AbortController
	public async uploadFile(
		file: File,
		onStart?: (uri: string, controller: { abort: () => void }) => void,
		onProgress?: (progress: number) => void,
		onComplete?: (response: FileUploadResponse) => void,
		onError?: (error: string) => void,
	): Promise<{ abort: () => void }> {
		// Get pre-signed URL for uploading the file
		const result = await this.getPresignedUploadUrl(file.name, file.type);

		if (isErr(result)) {
			throw result.error;
		}

		const http = new XMLHttpRequest();

		http.open(HttpMethod.Put, result.value.url, true);

		http.upload.onprogress = (event): void => {
			let progres = 0;
			if (event.total > 0) {
				progres = event.loaded / event.total;
			}
			onProgress?.(progres);
		};

		http.upload.onload = (): void => {
			onComplete?.(result.value);
		};

		http.upload.onerror = (): void => {
			onError?.("Something went wrong");
		};

		http.send(file);

		onStart?.(result.value.unified_uri, {
			abort: () => {
				http.abort();
			},
		});

		return {
			abort: () => {
				http.abort();
			},
		};
	}

	public async getFile(unifiedUri: string): Promise<PresignedUrl> {
		const response = unifiedUriSchema.safeParse(unifiedUri);

		if (!response.success) {
			throw new Error("Invalid unified URI");
		}

		const result = await this.client.get<PresignedUrl>(
			`${this._prefix}/download`,
			{
				query: {
					unified_uri: unifiedUri,
				},
			},
		);

		if (isErr(result)) {
			throw result.error;
		}

		return result.value;
	}

	public async deleteFile(
		unifiedUri: string,
	): Promise<Result<string | undefined>> {
		return this.client.delete<string>(this._prefix, {
			params: {
				unified_uri: unifiedUri,
			},
		});
	}
}
